// The board's own words.
//
// Every string the unit board says about a degraded state, an underwriting
// verdict or a battle bonus lives here, as pure functions over the state, so
// the sentences can be tested without a browser and so the ones that must not
// imply knowledge we lack cannot quietly acquire it.
//
// It stays out of `src/react-app/i18n/en.ts` on purpose. The board is
// deliberately English-only; the moment a board string lands in `en.ts` all
// nineteen locale files owe a translation and the parity test starts failing on
// a route nobody chose to localise.
//
// Three game rules that designs keep asserting and that this codebase has
// already checked against the live API. Nothing here may repeat them:
//
//   1. A hit does NOT cost a flat 10 health, so capacity is never
//      `floor(health / 10)`. The base cost is scaled by armour and dodge against
//      a softcap (see `@shared/mu-damage`), and hunger rather than the health bar
//      is what really bounds a contract: each hunger point buys a food worth a
//      slice of the bar. Reading current health alone undercounted a measured
//      767,943-damage burst by 13x. Say "hits" only from `hitsCapacity`, and keep
//      `readyDamage` (now) apart from `projectedDamage` (with food).
//   2. A pill is NOT a fixed plus or minus 60 percent. The game reports its own
//      percentage per skill, and an effect whose clock has run out is already out
//      of the damage figure. Take the number from the data (see `muEffects`),
//      never from a constant in a sentence.
//   3. The weakest gear slot does NOT cap a set. Damage comes from combat stats
//      that already include every contribution gear makes, and no per-set cap
//      exists anywhere in the game config. The gear strip is context on who is
//      equipped for what; it must never phrase itself as a damage rule.

import type { MuQueueRemoval, MuState } from "@shared/types";
import { ageWords, DAMAGE_STALE_MS } from "./dataAge";

/** What contracts actually ask for: of the last fifty won auctions, 1M appeared
 * thirty-two times, 500k nine, 2M four. The presets follow the real spread
 * rather than a round-number guess, and they are the same four the panel uses. */
export const TARGET_PRESETS = [500_000, 1_000_000, 1_500_000, 2_000_000];

export function compactDamage(value: number): string {
  const n = Math.round(Number(value) || 0);
  const abs = Math.abs(n);
  if (abs < 10_000) return n.toLocaleString("en-US");
  if (abs < 1_000_000) {
    const k = n / 1000;
    // 33.5k below a hundred thousand, 471k above: one decimal only where it
    // carries information. The design's own `fmt` draws the same line.
    return (abs < 100_000 ? k.toFixed(1).replace(/\.0$/, "") : Math.round(k)) + "k";
  }
  const m = n / 1_000_000;
  return (abs < 10_000_000 ? m.toFixed(2).replace(/\.?0+$/, "") : m.toFixed(1).replace(/\.0$/, "")) + "M";
}

export const exactDamage = (n: number) => Math.round(n).toLocaleString("en-US");

/** A percentage the way both surfaces write one: signed, one decimal. */
export function percentWords(factor: number): string {
  const pct = Math.round((factor - 1) * 1000) / 10;
  return (pct >= 0 ? "+" : "") + pct.toFixed(1) + "%";
}

/** The badge says what the unit HOLDS. Either this or the sentence below alone
 * has been the ambiguity all along: "gathering" describes people and reads as
 * "the unit is assembling for something", which is exactly the false
 * impression, and "no target" reuses the word the plan owns. */
export const PHASE_WORD: Record<MuState["phase"], string> = {
  idle: "no contract",
  planning: "plan only",
  won: "won, not started",
  live: "in progress",
  done: "completed",
};

/** The sentence beneath says what the member should DO. */
export const PHASE_SENTENCE: Record<MuState["phase"], string> = {
  idle: "Nothing to fight. A contract your unit wins lands here on its own.",
  planning:
    "Nothing has been won. Someone set a target so the unit can see whether it could cover one.",
  won: "Your unit holds this contract. Nobody has hit it yet.",
  live: "Damage is landing against this contract.",
  done: "This contract is finished.",
};

/**
 * Whether the unit could cover a contract, answered without being asked.
 *
 * This is the organizer's actual job on the most-visited screen in the product.
 * "Who is available" is not it; "can we cover a million" is.
 */
export function coverageVerdict(available: number): string {
  const covered = TARGET_PRESETS.filter((t) => available >= t);
  const short = TARGET_PRESETS.filter((t) => available < t);
  if (covered.length === 0) {
    return `Not enough for even a ${compactDamage(TARGET_PRESETS[0])} contract.`;
  }
  const best = compactDamage(covered[covered.length - 1]);
  if (short.length === 0) return `Enough for a ${best} contract, the largest they come.`;
  return `Enough for a ${best} contract. Not enough for ${compactDamage(short[0])}.`;
}

/** Clicking a preset is the what-if, and it is the whole underwriting form
 * replaced by one sentence. */
export function presetWhatIf(
  available: number,
  target: number,
  onlineNotRegistered: number,
  theirDamage: number,
): string {
  const gap = Math.max(0, target - available);
  const head =
    gap > 0
      ? `${exactDamage(available)} available against ${exactDamage(target)}: ${exactDamage(gap)} short.`
      : `${exactDamage(available)} available against ${exactDamage(target)}: covered.`;
  if (onlineNotRegistered <= 0) return head;
  return `${head} ${onlineNotRegistered} member${onlineNotRegistered === 1 ? " online is" : "s online are"} not registered; they carry ${exactDamage(theirDamage)}.`;
}

/** How long since a logged-in member of this unit was last heard from, and
 * what to make of it. `never` is a property of the unit, not an outage. */
export type RelayHealth = "never" | "fresh" | "silent";

export function relayHealth(relayedAt: number | null, nowMs: number): RelayHealth {
  if (relayedAt === null) return "never";
  return nowMs - relayedAt >= DAMAGE_STALE_MS ? "silent" : "fresh";
}

/** A clock time a person would say out loud, for a silence long enough that
 * "N hours ago" has stopped being useful. */
export function clockWords(atMs: number, nowMs: number): string {
  const at = new Date(atMs);
  const hhmm = at.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const days = Math.floor((startOfDay(nowMs) - startOfDay(atMs)) / 86_400_000);
  if (days <= 0) return hhmm;
  if (days === 1) return `yesterday ${hhmm}`;
  return `${at.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} ${hhmm}`;
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/**
 * The board cannot self-heal the way the panel can, because the reader is not a
 * relay. The wording is harder for that reason and must not be softened.
 */
export function frozenDamageParagraph(
  muName: string,
  dealt: number,
  relayedAt: number,
  nowMs: number,
): string {
  return `Damage is frozen at ${exactDamage(dealt)}. Nobody in ${muName} has had the game open for ${ageWords(nowMs - relayedAt).replace(/ ago$/, "")}. The real figure is probably higher. Nothing here will move until someone opens the game.`;
}

export function idleSilenceLine(relayedAt: number, nowMs: number): string {
  return `No member has relayed since ${clockWords(relayedAt, nowMs)}. Past contracts and payouts stay as they were until someone opens the game.`;
}

export function neverRelayedParagraph(muName: string): string {
  return `This unit has nobody relaying. Contract progress, payouts and who dealt what all come from the game itself, and only the browser extension can read it. Until one member of ${muName} installs it, this board shows who is available and what they commit to, and nothing else.`;
}

/* ------------------------------------------------------------------ bonus */

export interface BonusAuthor {
  /** The author's name, when the roster still carries them. Null when the row
   * predates the columns, or when they have left the unit. */
  name: string | null;
  setAtMs: number | null;
  /** True when the reader is the person who set it: being corrected is easier
   * to take when the system knows it is talking to you. */
  isYou: boolean;
}

/**
 * What became of a hand-set figure once a measurement superseded it.
 *
 * Three variants because the author and the set-at can both be absent on rows
 * written before the migration, and nothing here may imply knowledge we do not
 * have. A fourth changes person when the reader set it.
 */
export function bonusSupersededLine(
  setFactor: number,
  measuredFactor: number,
  author: BonusAuthor,
  nowMs: number,
): string {
  const was = percentWords(setFactor);
  const now = percentWords(measuredFactor);
  const when = author.setAtMs === null ? null : ageWords(nowMs - author.setAtMs);
  if (author.isYou) {
    return `You set ${was}${when ? " " + when : ""}. Members are hitting at ${now}, so projections use that instead.`;
  }
  if (author.name && when) return `${author.name} set ${was} ${when}. It is no longer applied.`;
  if (when) return `A figure of ${was} was set by hand ${when}. It is no longer applied.`;
  return `A figure of ${was} was set by hand. It is no longer applied.`;
}

/** The switch is announced, never silent. Not an OS notification: this is a
 * measurement stabilising, and interrupting the OS with it teaches people to
 * mute the two notifications that are facts. */
export function bonusSwitchBanner(
  setFactor: number,
  measuredFactor: number,
  author: BonusAuthor,
  nowMs: number,
): string {
  const was = percentWords(setFactor);
  const now = percentWords(measuredFactor);
  const when = author.setAtMs === null ? null : ageWords(nowMs - author.setAtMs);
  if (author.isYou) {
    return `Your ${was} has been replaced by the measured figure, ${now}. Members are hitting harder than you assumed, so projections now follow them.`;
  }
  if (author.name) {
    return `The battle bonus switched to the measured figure, ${now}. ${author.name} set ${was}${when ? " " + when : ""}; projections now use what members are actually hitting for.`;
  }
  return `The battle bonus switched to the measured figure, ${now}. A hand-set ${was} no longer applies.`;
}

/** C-5. Replacing somebody's figure names whose it is and when they set it, or
 * says plainly that nobody is recorded as having set it. */
export function replaceBonusConfirm(
  setFactor: number,
  nextPercent: number,
  author: BonusAuthor,
  nowMs: number,
): { question: string; yes: string; no: string } {
  const was = percentWords(setFactor);
  const next = (nextPercent >= 0 ? "+" : "") + nextPercent.toFixed(2).replace(/\.?0+$/, "") + "%";
  const when = author.setAtMs === null ? null : ageWords(nowMs - author.setAtMs);
  if (author.name && when) {
    return {
      question: `Replace the ${was} ${author.name} set ${when} with ${next}? Every projection on both screens uses it.`,
      yes: "Yes, replace it",
      no: `Keep ${author.name}'s`,
    };
  }
  if (when) {
    return {
      question: `Replace the ${was} set by hand ${when} with ${next}? Every projection on both screens uses it.`,
      yes: "Yes, replace it",
      no: "Keep it",
    };
  }
  return {
    question: `Replace the hand-set ${was} with ${next}? Nobody is recorded as having set it, and every projection on both screens uses it.`,
    yes: "Yes, replace it",
    no: "Keep it",
  };
}

/** C-6. Clearing it is not the same act and does not read the same. */
export function clearBonusConfirm(
  setFactor: number,
  author: BonusAuthor,
  nowMs: number,
): { question: string; yes: string; no: string } {
  const was = percentWords(setFactor);
  const when = author.setAtMs === null ? null : ageWords(nowMs - author.setAtMs);
  const tail =
    " Projections fall back to members' raw attack figures, which understate what they will actually deal.";
  if (author.name && when) {
    return {
      question: `Clear the ${was} ${author.name} set ${when}?${tail}`,
      yes: "Yes, clear it",
      no: `Keep ${author.name}'s`,
    };
  }
  if (when) {
    return { question: `Clear the ${was} set by hand ${when}?${tail}`, yes: "Yes, clear it", no: "Keep it" };
  }
  return { question: `Clear the hand-set ${was}?${tail}`, yes: "Yes, clear it", no: "Keep it" };
}

/* ------------------------------------------------- destructive confirmations */

/** C-3. Both halves are true and both matter: the damage stays counted because
 * the split is seeded from the damage map, and the shortfall grows because what
 * they still owed leaves the queue. The second half is the one that changes the
 * decision. */
export interface Confirmation {
  question: string;
  /** What the button that goes through with it says. Buttons reading "OK" and
   * "Cancel" make the reader re-read the question to work out which is which,
   * so every confirmation states its own two words. */
  yes: string;
  no: string;
}

export function removeMemberConfirm(opts: {
  username?: string;
  committed: number;
  delivered?: number;
  pledged?: boolean;
  addedByTeammate?: string | null;
  live: boolean;
  /** Leaving your own queue is a lighter act than taking somebody else off
   * one, and it is the same control, so it is the same function. */
  isMe?: boolean;
  shortfall?: number;
}): Confirmation {
  if (opts.isMe) {
    return {
      question: opts.live
        ? `Leave the queue? You committed ${exactDamage(opts.committed)} and the unit is ${exactDamage(opts.shortfall ?? 0)} short.`
        : "Take your name off the available list?",
      yes: opts.live ? "Yes, leave" : "Yes",
      no: opts.live ? "Stay" : "Stay on it",
    };
  }
  const name = opts.username ?? "them";
  // The list they come off is named, because it is not the same list in both
  // states: off a queue is off a commitment the unit is counting on, off the
  // available list is only a withdrawn offer.
  const off = `Take ${name} off the ${opts.live ? "queue" : "available list"}?`;
  const delivered = opts.delivered ?? 0;
  let question;
  if (delivered > 0) {
    const owed = Math.max(0, opts.committed - delivered);
    question = `${off} Their ${exactDamage(delivered)} dealt stays counted. The ${exactDamage(owed)} they still owed leaves the queue, so the unit will read ${exactDamage(owed)} shorter.`;
  } else if (opts.addedByTeammate) {
    question = `${off} ${opts.addedByTeammate} put them in for ${exactDamage(opts.committed)}.`;
  } else if (!opts.pledged) {
    question = `${off} They are in at whatever their health affords, currently ${exactDamage(opts.committed)}.`;
  } else {
    question = `${off} They committed ${exactDamage(opts.committed)} themselves.`;
  }
  return { question, yes: "Yes, take them off", no: "Keep them" };
}

/**
 * What a member is told when they come back to find themselves out of the queue
 * they remember joining. The panel's `removalNotice` says the same thing in the
 * same words; both read the row the service now keeps for exactly this.
 *
 * The offline drop is the case that needed it most: it is the service's own
 * rule, it looks identical to a teammate taking somebody off, and until now
 * nobody was told it had happened at all, let alone why.
 */
export function removalNotice(
  removal: MuQueueRemoval,
  name: string | null,
  live: boolean,
): { text: string; rejoinAt: number | null; rejoinLabel: string } {
  const off = live ? "off the queue" : "off the available list";
  const text =
    removal.reason === "offline"
      ? `The unit was fighting and the game showed you offline, so you were taken ${off}. Registering again holds while you are away.`
      : name
        ? `${name} took you ${off}.`
        : `You were taken ${off}.`;
  const at = Math.round(Number(removal.committed) || 0);
  return {
    // Their intent has not changed, whoever ended it, so the way back is one
    // click at the figure they had.
    text,
    rejoinAt: at > 0 ? at : null,
    rejoinLabel: at > 0 ? `Rejoin at ${compactDamage(at)}` : live ? "Rejoin" : "I am available",
  };
}

/** C-4. Lighter, because it is your own health and your own promise. */
/**
 * The two kinds of commitment, said out loud.
 *
 * Both have always existed — a queue row with no pledged figure is recomputed
 * from health, food and gear on every poll, one with a figure is frozen at what
 * somebody typed — but which you got was decided by whether you happened to
 * touch the field, and the only surviving trace was an underline. It is a real
 * difference in what the unit may count on, so it is a choice now.
 */
export function commitModes(projected: number): {
  following: { id: "following"; label: string; detail: string };
  fixed: { id: "fixed"; label: string; detail: string };
} {
  return {
    following: {
      id: "following",
      label: "Follows my health",
      // Naming the moment is what stops a live figure reading as a promise
      // about a number.
      detail: projected
        ? `${exactDamage(Math.round(projected))} right now, and it follows your health and food`
        : "whatever your health and food afford at the time",
    },
    fixed: {
      id: "fixed",
      label: "A fixed number",
      detail: "Your own figure, held whatever your health does",
    },
  };
}

/** What a member is told they committed to. The same figure means two different
 * things depending on the kind, so the kind is in the sentence. */
export function commitConfirmation(
  damage: number,
  fixed: boolean,
  muName: string,
  shortfall: number,
  live: boolean,
): string {
  const amount = exactDamage(Math.round(damage));
  const head = fixed
    ? `You are in for a fixed ${amount}.`
    : `You are in for ${amount}, following your health.`;
  if (!live) return `${head} You are registered for the next contract.`;
  if (shortfall > 0) return `${head} ${muName} is still ${exactDamage(shortfall)} short.`;
  return `${head} ${muName} is covered.`;
}

/** How a queue row reads to somebody who did not write it. The underline
 * carries this visually; a member reading a screen reader needs the word. */
/**
 * What kind of commitment YOUR entry is, without saying the figure again.
 *
 * The standing card carries the number in the largest type on the screen and
 * the source bars carry it a second time; the bar's sentence used to open with
 * it a third time. What it alone can say is whether the figure is frozen or
 * follows your health, which is the one thing the card cannot show.
 */
export function commitKindMine(pledged: boolean): string {
  return pledged
    ? "Fixed: it stays at this figure whatever your health does."
    : "Following your health and food, so it moves with them.";
}

export function commitKindWords(pledged: boolean, damage: number): string {
  return pledged
    ? `A fixed ${exactDamage(Math.round(damage))}, held whatever their health does`
    : `${exactDamage(Math.round(damage))}, following their health and food`;
}


/** C-8. Not destructive in the delete sense, but it overwrites another person's
 * stated intent, and confirmation is the only guard this product has. */
export function setOthersCommitmentConfirm(
  username: string,
  next: number,
  committed: number,
  pledged: boolean,
): Confirmation {
  const head = `Set ${username}'s commitment to ${exactDamage(next)}?`;
  return {
    question: pledged
      ? `${head} They committed ${exactDamage(committed)} themselves.`
      : `${head} They are currently in at whatever their health affords.`,
    yes: "Yes, set it",
    no: "Cancel",
  };
}

/** C-1. The current string, "Clear the target?", names neither the figure nor
 * the person. */
export function clearTargetConfirm(
  target: number,
  author: string | null,
  isYou: boolean,
): Confirmation {
  const question = isYou
    ? `Clear the ${exactDamage(target)} target you set on this battle?`
    : author
      ? `Clear the ${exactDamage(target)} target ${author} set on this battle?`
      : `Clear the ${exactDamage(target)} target a former member set on this battle?`;
  return { question, yes: "Yes, clear it", no: "Keep it" };
}

/** C-9. */
export function replaceTargetConfirm(
  author: string | null,
  current: number,
  next: number,
): string {
  const whose = author ? `${author}'s ` : "the ";
  return `Replace ${whose}${exactDamage(current)} target with ${exactDamage(next)} on this battle?`;
}

/** C-7. The most destructive action in the product: it revokes the link every
 * member holds, including the page performing the revocation. */
export function replaceLinkConfirm(muName: string): string {
  return `Replace this link? Everyone in ${muName} loses the one they have, including this page. Only do this if the link got out of your channel.`;
}

/* ----------------------------------------------------------- the vocabulary */

/**
 * The words both surfaces use for the same things.
 *
 * One vocabulary, stated once, because a wording change that reaches one
 * surface and not the other is the failure this whole design exists to prevent.
 * `MUQ_FORMAT.WORDS` in `extension-mu/format.js` is this object: that file is
 * built from this module rather than written beside it.
 *
 * Not a general string table: only the terms that name a thing the reader has
 * to recognise across screens belong here. A sentence built for one place still
 * lives with the function that builds it.
 */
export const WORDS = {
  /** The primary action. The verb `Queue` as a roster button is retired; adding
   * somebody else is `Add`. */
  queueUp: "queue up",
  add: "Add",
  /** The two ledger groups. Every member of the unit is in one of them. */
  inQueue: "in queue",
  notInQueue: "not in queue",
  /** The editable figure: what this member still owes, not what they promised.
   * The bar reports the same quantity (spec invariant 1). */
  leftToDeal: "left to deal",
  /** What the queue holds before the award. */
  committed: "committed",
  /** What the contract paid, once it is over. */
  atClose: "at close",
  /** Filing closes the contract out and leaves every seat, figure and flag
   * exactly where it was (ruling 1). The label says so because the button
   * before it read like a delete. */
  file: "file it · keep the queue",
  /** A battle nobody has fought has no measured multiplier, and the game never
   * states one. This is what that reads as, and it is never a 0 percent. */
  bonusUnknown: "bonus unknown",
  /** The standing-queue wordings, for a unit holding nothing. A hand-set target
   * borrows the maths of an open auction and none of its copy. */
  noContract: "no contract",
  noAuction: "no auction · standing queue",
  noRate: "no contract · no rate",
  nothingStaked: "nothing staked",
  /** A figure that does not exist, as opposed to one that is zero. */
  none: "—",
  /** A member carrying neither a pill nor a penalty. A fact about them, not an
   * absence to be left blank. */
  noPill: "no pill",
  /** The payout column head, before and after the contract settles. */
  wouldEarn: "would earn",
  paid: "paid",
  /** What a failed or lost contract pays, however much was dealt. */
  nothing: "nothing",
} as const;

/**
 * The screen's own line: what this screen is about, and where the unit stands.
 *
 * The unit header above it names the unit and the tool; this names the SUBJECT
 * — the battle the queue is pointed at — and the state sentence beside it says
 * what is happening to it. The design puts both on one line with the screen
 * strip, so a reader who lands on the board knows what they are looking at
 * before they read a figure.
 */
export const screenTitle = (battleName: string | null) => `Queue · ${battleName || WORDS.none}`;

/** The state sentence beside the title, in the words of the phase it states. */
export function screenMeta(input: {
  phase: LedgerPhaseWord;
  handSet: boolean;
  queueCount: number;
}): string {
  const { phase, handSet, queueCount } = input;
  if (handSet || phase === "none") return `standing queue · ${queueCount} in, nothing posted yet`;
  if (phase === "open") return `auction running · ${queueCount} in the queue, nothing bid yet`;
  if (phase === "bid") return "bid placed · waiting on the auction";
  if (phase === "lost") return "another unit underbid us";
  if (phase === "settled") return "settled";
  return `we won the contract · ${queueCount} in the queue`;
}

/** The phases the screen line knows about. The same six the ledger uses; kept
 * as a local name so this file does not depend on the ledger's module. */
export type LedgerPhaseWord = "none" | "open" | "bid" | "fulfilling" | "lost" | "settled";

/**
 * The two group strips, and the two totals that close them.
 *
 * The design gives each group a head that names it and a subtotal band that
 * states its figures, then closes the ledger with the whole unit. The head
 * never carries a committed figure for the group outside the queue: capacity
 * nobody promised is not a promise, and printing it as one is the same lie in
 * two places.
 */
export const groupHeadWords = (id: "in" | "out", count: number) =>
  id === "in" ? `${WORDS.inQueue} · ${count}` : `${WORDS.notInQueue} · nothing committed`;

/** The band under a group: what it dealt, and what it still owes. */
export function subtotalWords(input: {
  id: "in" | "out";
  count: number;
  dealt: number;
  leftToDeal: number;
  committed: number;
  preAward: boolean;
  closed: boolean;
}): { head: string; figure: string } {
  const { id, count, dealt, leftToDeal, committed, preAward, closed } = input;
  if (id === "out") {
    return {
      head: `${WORDS.notInQueue} · ${count} · not counted`,
      figure: dealt > 0 ? compactDamage(dealt) : `${compactDamage(committed)} free`,
    };
  }
  const owed = closed
    ? leftToDeal > 0
      ? `${compactDamage(leftToDeal)} short`
      : "all met"
    : preAward
      ? `${compactDamage(committed)} in`
      : `owed ${compactDamage(leftToDeal)}`;
  return {
    head: `${WORDS.inQueue} · ${count}`,
    figure: `${dealt > 0 ? compactDamage(dealt) : WORDS.none} · ${owed}`,
  };
}

/** The row that closes the ledger: everybody, queued or not. */
export function unitRowWords(input: {
  count: number;
  capacity: number;
  dealt: number;
}): { head: string; figure: string } {
  return {
    head: `whole unit · ${input.count} · ${compactDamage(input.capacity)} capacity`,
    figure: input.dealt > 0 ? compactDamage(input.dealt) : WORDS.none,
  };
}


/** Members outside the queue who could still be asked. Spare capacity, not
 * spare people: the figure the unit could still reach for. */
export const freeWords = (count: number) => `${count} free`;

export const moneyWords = (value: number) => "$" + value.toFixed(2);

/** The rate a contract pays, per thousand damage. */
export const rateWords = (perK: number | null) =>
  perK === null ? WORDS.noRate : `${moneyWords(perK)} / 1k`;

/** The measured battle multiplier, or the honest absence of one. */
export const bonusWords = (factor: number | null) =>
  factor === null ? WORDS.bonusUnknown : percentWords(factor);

/**
 * What a member would earn, and what kind of claim that is.
 *
 * `≈` and muted while the contract is being fulfilled, because it is a
 * projection off a rate that has not paid yet; unprefixed once the game has
 * settled it; and `nothing` on a failure or a lost auction however much was
 * dealt, which is the fact the ledger exists to be honest about.
 */
export function payoutWords(
  phase: "open" | "bid" | "fulfilling" | "lost" | "settled" | "none",
  amount: number | null,
): { text: string; tone: "projected" | "paid" | "nothing" | "empty" } {
  if (phase === "lost") return { text: WORDS.nothing, tone: "nothing" };
  // No contract means no claim on anybody's money, so the column is empty
  // rather than reading as a zero somebody could have earned.
  if (phase === "none" || phase === "open" || phase === "bid" || amount === null) {
    return { text: "", tone: "empty" };
  }
  if (phase === "settled") return { text: moneyWords(amount), tone: "paid" };
  return { text: "≈ " + moneyWords(amount), tone: "projected" };
}

/**
 * The line that keeps a unit holding several contracts honest.
 *
 * Coverage is per contract and reads one pool of commitments, so two contracts
 * being fulfilled can each print "the queue covers it" while the unit is short
 * overall. Null with one contract or none: there the coverage bar already tells
 * the truth and repeating it is noise.
 */
export function unitWideLine(totals: {
  manyLive: boolean;
  liveCount: number;
  committed: number;
  expected: number;
  shortOverall: number;
}): string | null {
  if (!totals.manyLive) return null;
  const head = `${compactDamage(totals.committed)} committed · ${compactDamage(totals.expected)} expected across ${totals.liveCount} live contracts`;
  return totals.shortOverall > 0
    ? `${head} · ${compactDamage(totals.shortOverall)} short overall`
    : `${head} · covered overall`;
}

/** The verdict a standing queue gets: what it holds, and that it holds it
 * against nothing yet. */
export const noContractVerdict = (committed: number) =>
  `${compactDamage(committed)} committed · ${WORDS.noContract}`;

/**
 * How long an open auction has left.
 *
 * These run five to twenty-five minutes and a bid inside the last minute pushes
 * the close out by another, so the countdown is the loudest thing on an open
 * row and it has to survive jumping forward without reading as a bug. Seconds
 * under a minute, because that last minute is where the auction is actually
 * decided; minutes above it, because a second-by-second clock on a ten-minute
 * wait is noise.
 *
 * `extendable` is the reader's warning that the number may go up: a clock that
 * jumps with no explanation is a clock nobody trusts again.
 */
export function countdownWords(
  secondsLeft: number | null,
  extendable = false,
): { text: string; urgent: boolean; note: string | null } {
  if (secondsLeft === null) return { text: "no close stated", urgent: false, note: null };
  if (secondsLeft <= 0) return { text: "closing", urgent: true, note: null };
  const note = extendable ? "a bid now pushes the close out a minute" : null;
  if (secondsLeft < 60) return { text: `${secondsLeft}s`, urgent: true, note };
  const minutes = Math.floor(secondsLeft / 60);
  return { text: `${minutes}m`, urgent: minutes < 2, note };
}

/* -------------------------------------------------------- the commit block */

/**
 * What kind of commitment the reader is making, said in one word.
 *
 * A percentage keeps tracking live capacity as pills expire and health moves; a
 * typed number stays put. Which one you have is a real difference in what the
 * unit may count on, and it used to be decided by whether you happened to touch
 * the field, with an underline as the only trace.
 */
export const commitWord = (tracking: boolean) =>
  tracking ? "commit · tracking" : "commit · fixed";

/**
 * Where the reader themselves stands, in the block that changes it.
 *
 * The design states this once, in the commit block, beside the control that
 * acts on it. A headline card above the progress said the same figure a second
 * time and the verdict chip a third, so the number a member came to check
 * appeared three times in three shapes and moved in none of them.
 */
export function ownStandingLine(input: {
  username: string;
  inQueue: boolean;
  committed: number;
  dealt: number;
}): string {
  const who = `you · ${input.username}`;
  if (!input.inQueue) return `${who} · not in the queue`;
  return `${who} · signed up for ${compactDamage(input.committed)} · dealt ${compactDamage(input.dealt)}`;
}

/**
 * What a finished contract says, over the button that files it.
 *
 * Filing closes the contract out and leaves every seat, figure and pledge where
 * it was (ruling 1) — the queue belongs to the unit, not to any contract — which
 * is why the button says so rather than reading like a delete.
 */
export function settledBand(input: {
  reached: boolean;
  dealt: number;
  target: number;
}): string {
  if (input.reached) {
    const over = input.dealt - input.target;
    return over > 0
      ? `Target met, and ${compactDamage(over)} past it.`
      : "Target met.";
  }
  return `Over at ${compactDamage(input.dealt)} of ${compactDamage(input.target)}.`;
}

/** The primary action, which changes once you are in. */
export const signLabel = (inQueue: boolean) => (inQueue ? "update" : WORDS.queueUp);

/** Shares of what this member can actually do. There is no preset above all of
 * it: over-committing is done by typing a number, which is legitimate because
 * food and pills raise capacity. */
export const COMMIT_PRESETS = [0.25, 0.5, 1] as const;

export const presetLabel = (share: number, value: number) =>
  share === 1 ? `all ${compactDamage(value)}` : `${Math.round(share * 100)}% ${compactDamage(value)}`;

/**
 * Why the figure is what it is, and what it will do next.
 *
 * The capacity half explains a number that looks surprising; the second half
 * says whether it will move on its own, which is the thing a reader cannot see
 * by looking at it.
 */
export function affordLine(input: {
  hits: number;
  perHit: number;
  affords: number;
  tracking: boolean;
}): string {
  return (
    `${input.hits} hits of health at ${compactDamage(input.perHit)} ≈ ${compactDamage(input.affords)} · ` +
    (input.tracking
      ? "a percentage follows your capacity as it changes"
      : "a typed number stays put · promising more than capacity is fine")
  );
}

/* --------------------------------------------------------- the terms strip */

/**
 * A contract's terms, read-only, in the words the design system uses for them.
 *
 * Every one of these comes from the game and none of them can be typed over.
 * The one exception is a target somebody set by hand, which is not a contract
 * at all: it says so in every cell rather than borrowing an auction's copy for
 * figures that do not exist.
 */
export function contractTerms(input: {
  /** No contract in hand: the figures are a target somebody typed. */
  handSet: boolean;
  expected: number;
  ratePerK: number | null;
  payout: number | null;
  deposit: number | null;
  /** Where the deposit stands, which is the whole reason it is on screen. */
  phase: "none" | "open" | "bid" | "lost" | "fulfilling" | "completed" | "failed";
}): {
  expectedLabel: string;
  expected: string;
  rate: string;
  payment: string;
  deposit: string;
  depositState: string;
} {
  const { handSet, phase } = input;
  return {
    expectedLabel: handSet ? "expected damage · set by hand" : "expected damage",
    expected: exactDamage(input.expected),
    rate: handSet || input.ratePerK === null ? WORDS.noRate : rateWords(input.ratePerK),
    payment: handSet || input.payout === null ? WORDS.none : moneyWords(input.payout),
    deposit: handSet || input.deposit === null ? WORDS.none : moneyWords(input.deposit),
    // What the unit stands to lose, which is the only reason the figure is
    // worth a line. "Not staked" and "at risk" are different states, and a
    // contract that ended says which way it went.
    depositState: handSet
      ? WORDS.nothingStaked
      : phase === "completed"
        ? "returned"
        : phase === "failed"
          ? "lost"
          : phase === "lost"
            ? "not staked"
            : phase === "open" || phase === "bid" || phase === "none"
              ? "not staked yet"
              : "at risk",
  };
}

/* ------------------------------------------------------------- the verdict */

/**
 * The one chip beside the big figure, and the only sentence that answers the
 * question the tool exists for: does what the queue holds cover what is in
 * front of it.
 *
 * The wording is the design system's, phase by phase, because each phase is a
 * different claim. A unit that has not bid has committed something against
 * nothing; a unit fulfilling is short or covered; a settled one is over or
 * under, and by how much. "Outbid" is reserved for an auction the unit actually
 * bid on, because it is a claim about a bid.
 */
export type VerdictTone = "plan" | "gone" | "done" | "failed" | "short" | "covered";

export function verdictChip(input: {
  /** What the unit holds. `none` covers a standing queue and a hand-set target
   * alike: both are commitments against no contract. */
  phase: "none" | "open" | "bid" | "lost" | "fulfilling" | "completed" | "failed";
  /** What the queue has committed. */
  committed: number;
  /** What is still uncovered against the target. */
  open: number;
  /** Damage past the target on a contract that completed. */
  over?: number;
  /** How far short a failed contract ended. */
  short?: number;
}): { text: string; tone: VerdictTone } {
  const { phase, committed, open } = input;
  if (phase === "lost") return { text: "outbid · nothing to deliver", tone: "gone" };
  if (phase === "completed") {
    return { text: `completed · overkill ${compactDamage(input.over ?? 0)}`, tone: "done" };
  }
  if (phase === "failed") {
    return { text: `failed · short ${compactDamage(input.short ?? 0)}`, tone: "failed" };
  }
  // Before the award nothing has been dealt and nothing is owed: what the unit
  // has is a commitment, and the chip says what it is against.
  if (phase === "none") {
    return { text: `${compactDamage(committed)} committed · ${WORDS.noContract}`, tone: "plan" };
  }
  if (phase === "open" || phase === "bid") {
    return {
      text: `${compactDamage(committed)} committed · ${phase === "bid" ? "bid in" : "not bid yet"}`,
      tone: "plan",
    };
  }
  return open > 0
    ? { text: `short ${compactDamage(open)}`, tone: "short" }
    : { text: "queue covers it", tone: "covered" };
}

/**
 * The three-part row under the bar: what happened, what was promised, what is
 * still open. The words change once the contract is closed, because at that
 * point they are a record rather than a plan.
 */
export function summaryRow(input: {
  dealt: number;
  promised: number;
  open: number;
  over?: number;
  closed?: boolean;
}): { dealt: string; promised: string; open: string } {
  const { dealt, promised, open, closed } = input;
  const over = input.over ?? 0;
  return {
    dealt: `${compactDamage(dealt)} dealt`,
    promised: `${compactDamage(promised)} ${closed ? "promised at close" : "promised"}`,
    open: closed
      ? over > 0
        ? `+${compactDamage(over)} over target`
        : `${compactDamage(open)} short at close`
      : `${compactDamage(open)} open`,
  };
}

/* --------------------------------------------------------- the relay band */

/**
 * How old the relayed figures are, in three tiers.
 *
 * Damage only moves while a member has the game open, so these figures can sit
 * still for an hour while looking perfectly current. Under ten minutes is
 * ordinary and the band stays quiet; from ten the dealt segment switches from
 * solid to hatch, because it is no longer something that happened so much as
 * something that was true; from thirty it is an outage, and the band carries
 * the one action that fixes it.
 *
 * Deliberately separate from the header's age. The header says when the ROSTER
 * was fetched from the game, this says how old the RELAYED damage is, and
 * wording them the same taught readers the two clocks were one.
 */
export const RELAY_AGING_MS = 10 * 60_000;
export const RELAY_STALE_MS = 30 * 60_000;

export type RelayTier = "never" | "live" | "aging" | "stale";

export function relayTier(relayedAt: number | null, nowMs: number): RelayTier {
  if (relayedAt === null) return "never";
  const age = Math.max(0, nowMs - relayedAt);
  if (age >= RELAY_STALE_MS) return "stale";
  if (age >= RELAY_AGING_MS) return "aging";
  return "live";
}

/**
 * What the band says, and whether it has anything to offer.
 *
 * `never` is a property of the unit rather than an outage: nobody here has ever
 * run the extension, and no amount of waiting changes it, so the action is to
 * install rather than to open the game.
 */
export function relayBand(
  relayedAt: number | null,
  nowMs: number,
  muName: string,
): { tier: RelayTier; text: string; action: string | null } {
  const tier = relayTier(relayedAt, nowMs);
  if (tier === "never") {
    return {
      tier,
      text: `Nobody in ${muName} is relaying. Damage and payouts come from the game itself, and only the extension can read them.`,
      action: "How to relay",
    };
  }
  const age = ageWords(Math.max(0, nowMs - (relayedAt as number)));
  if (tier === "live") return { tier, text: `Figures relayed ${age}.`, action: null };
  if (tier === "aging") {
    return { tier, text: `Figures are ${age.replace(/ ago$/, "")} old.`, action: null };
  }
  return {
    tier,
    text: `Nobody in ${muName} has had the game open for ${age.replace(/ ago$/, "")}. Nothing here moves until somebody does.`,
    action: "Open the game",
  };
}

/* --------------------------------------------------------------- the screens */

/**
 * The four screens, in the order they are read, with the words on their tabs.
 *
 * The same list, in the same order, with the same labels as `MUQ_FORMAT.TABS`
 * in the extension. A tab named one thing beside the game and another on a
 * phone is two products. There is one implementation of it now: the
 * extension's bundle is built from this file.
 *
 * Four tabs, no nesting (spec ruling 2). `Queue` answers the live question and
 * is where every open lands; `Contract` is the battle, the auctions open in it
 * and the terms of the one the unit holds. History keeps the record and
 * Settings is set once, and neither belongs on the screen that answers the live
 * question.
 */
export const TABS = [
  { id: "queue", label: "Queue" },
  { id: "contract", label: "Contract" },
  { id: "history", label: "History" },
  { id: "settings", label: "Settings" },
] as const;

export type TabId = (typeof TABS)[number]["id"];

/**
 * The headline every state carries: whether you are in, and for how much.
 *
 * One queue per unit and it is permanent, so the only thing about it that can
 * be true of YOU is in or out. The figure is what you still owe once a contract
 * is live, and the whole projection before that.
 */
export function standingHeadline(
  mine: { committed: number; owed: number } | null | undefined,
  live: boolean,
): { inQueue: boolean; damage: number; text: string } {
  if (!mine) return { inQueue: false, damage: 0, text: "You are not in the queue" };
  const damage = Math.round(live ? mine.owed : mine.committed);
  return { inQueue: true, damage, text: `You are in for ${exactDamage(damage)}` };
}

/** What the unit as a whole is holding, under the headline. */
export function standingSubline(count: number, committed: number): string {
  return `${count} in queue, ${compactDamage(committed)} committed`;
}

/**
 * The one verdict box, and the only question the tool actually answers.
 *
 * With a contract live it is whether what the queue holds still covers what is
 * left. Without one it is what the unit could cover if it bid, judged against a
 * target when somebody has named one and against the spread of real contracts
 * when nobody has.
 */
export function coverageBox(opts: {
  live: boolean;
  available: number;
  remaining: number;
  judgeAgainst: number | null;
}): { tone: "ok" | "short"; text: string } {
  const { live, available, remaining, judgeAgainst } = opts;
  if (live) {
    if (remaining <= 0) return { tone: "ok", text: "The target is met. Nothing left to deal." };
    const covers = available >= remaining;
    return {
      tone: covers ? "ok" : "short",
      text:
        (covers
          ? "The queue still covers what is left, "
          : "The queue no longer covers what is left, ") +
        `${compactDamage(available)} against ${compactDamage(remaining)}`,
    };
  }
  if (!judgeAgainst || judgeAgainst <= 0) {
    return {
      tone: available >= TARGET_PRESETS[0] ? "ok" : "short",
      text: coverageVerdict(available),
    };
  }
  const gap = Math.abs(available - judgeAgainst);
  return available >= judgeAgainst
    ? {
        tone: "ok",
        text: `Covers a ${compactDamage(judgeAgainst)} contract, ${compactDamage(gap)} to spare`,
      }
    : {
        tone: "short",
        text: `Short of a ${compactDamage(judgeAgainst)} contract, needs ${compactDamage(gap)} more`,
      };
}
